import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  getAdminMenus,
  createMenu,
  updateMenu,
  deleteMenu,
  getAllContent,
} from "../../services/api";
import "./Dashboard.css";

const MENU_TYPES = [
  { value: "static", label: "Statis (Link / URL)" },
  { value: "content", label: "Konten dari database" },
];

const emptyForm = {
  label: "",
  label_en: "",
  parent_id: "",
  menu_type: "static",
  url: "",
  content_jenis: "",
  target: "_self",
  urutan: 0,
  is_active: true,
};

const normalizeMenuId = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const sortMenus = (list) =>
  [...list].sort((a, b) => {
    const orderDiff = (a.urutan ?? 0) - (b.urutan ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return (a.id ?? 0) - (b.id ?? 0);
  });

const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [contentJenisList, setContentJenisList] = useState([]);

  const fetchMenus = useCallback(async ({ silent = false, bustCache = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await getAdminMenus({ bustCache: silent || bustCache });
      if (res.data.success) {
        setMenus(sortMenus(res.data.data || []));
      }
    } catch {
      if (!silent) setError("Gagal memuat data menu");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenus();
    // Ambil daftar jenis konten yang sudah ada di tabel Content
    getAllContent()
      .then((res) => {
        if (res.data.success) {
          // Deduplikasi berdasarkan jenis, ambil record terbaru per jenis
          const seen = new Set();
          const list = res.data.data
            .filter((c) => c.jenis && !seen.has(c.jenis) && seen.add(c.jenis))
            .map((c) => ({ jenis: c.jenis, label: c.judul || c.jenis }));
          setContentJenisList(list);
        }
      })
      .catch(() => {});
  }, [fetchMenus]);

  const topLevelMenus = sortMenus(
    menus.filter((m) => normalizeMenuId(m.parent_id) == null),
  );
  const childrenOf = (parentId) =>
    sortMenus(
      menus.filter(
        (m) => normalizeMenuId(m.parent_id) === normalizeMenuId(parentId),
      ),
    );

  const handleShowModal = (menu = null) => {
    if (menu) {
      setCurrentMenu(menu);
      setFormData({
        label: menu.label || "",
        label_en: menu.label_en || "",
        parent_id: menu.parent_id || "",
        menu_type: menu.menu_type || "static",
        url: menu.url || "",
        content_jenis: menu.content_jenis || "",
        target: menu.target || "_self",
        urutan: menu.urutan ?? 0,
        is_active: menu.is_active === true || menu.is_active === 1,
      });
    } else {
      setCurrentMenu(null);
      setFormData(emptyForm);
    }
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentMenu(null);
    setFormData(emptyForm);
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        label: formData.label,
        label_en: formData.label_en || null,
        parent_id: formData.parent_id ? parseInt(formData.parent_id, 10) : null,
        menu_type: formData.menu_type,
        url: formData.menu_type === "content" ? null : formData.url || null,
        content_jenis:
          formData.menu_type === "content"
            ? formData.content_jenis || null
            : null,
        target: formData.target,
        urutan: parseInt(formData.urutan, 10) || 0,
        is_active: formData.is_active ? 1 : 0,
      };

      if (currentMenu) {
        await updateMenu(currentMenu.id, payload);
        setMenus((prev) =>
          sortMenus(
            prev.map((item) =>
              item.id === currentMenu.id
                ? {
                    ...item,
                    ...payload,
                    is_active: payload.is_active,
                  }
                : item,
            ),
          ),
        );
      } else {
        const res = await createMenu(payload);
        const newId = res.data?.id;
        if (newId) {
          setMenus((prev) =>
            sortMenus([
              ...prev,
              {
                id: newId,
                ...payload,
                is_active: payload.is_active,
              },
            ]),
          );
        }
      }

      handleCloseModal();
      await fetchMenus({ silent: true, bustCache: true });
    } catch (err) {
      setError(
        "Gagal menyimpan menu: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (menu) => {
    const hasChildren = menus.some(
      (m) => normalizeMenuId(m.parent_id) === normalizeMenuId(menu.id),
    );
    const msg = hasChildren
      ? `Menu "${menu.label}" memiliki sub-menu. Menghapus akan menghapus sub-menu juga. Lanjutkan?`
      : `Hapus menu "${menu.label}"?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteMenu(menu.id);
      setMenus((prev) =>
        prev.filter(
          (m) =>
            m.id !== menu.id &&
            normalizeMenuId(m.parent_id) !== normalizeMenuId(menu.id),
        ),
      );
      await fetchMenus({ silent: true, bustCache: true });
    } catch (err) {
      alert(
        "Gagal menghapus menu: " + (err.response?.data?.message || err.message),
      );
    }
  };

  // Potential parent options (exclude current item and its descendants)
  const parentOptions = menus.filter(
    (m) =>
      normalizeMenuId(m.parent_id) == null &&
      (!currentMenu || m.id !== currentMenu.id),
  );

  const renderMenuRow = (menu, depth = 0) => {
    const children = childrenOf(menu.id);
    const rows = [
      <tr key={menu.id}>
        <td>
          {depth > 0 && (
            <span style={{ marginLeft: depth * 20, color: "#999" }}>└ </span>
          )}
          {menu.label}
        </td>
        <td>
          <Badge bg={menu.menu_type === "content" ? "info" : "secondary"}>
            {menu.menu_type === "content" ? "Konten" : "Statis"}
          </Badge>
        </td>
        <td
          style={{ maxWidth: 200, wordBreak: "break-all", fontSize: "0.85rem" }}
        >
          {menu.menu_type === "content" ? (
            <span className="text-muted">/halaman/{menu.content_jenis}</span>
          ) : (
            menu.url || <span className="text-muted">-</span>
          )}
        </td>
        <td>{menu.urutan}</td>
        <td>
          <Badge
            bg={
              menu.is_active === true ||
              menu.is_active === 1 ||
              menu.is_active === "1"
                ? "success"
                : "danger"
            }
          >
            {menu.is_active === true ||
            menu.is_active === 1 ||
            menu.is_active === "1"
              ? "Aktif"
              : "Nonaktif"}
          </Badge>
        </td>
        <td>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-1"
            onClick={() => handleShowModal(menu)}
          >
            <i className="fas fa-edit"></i>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(menu)}
          >
            <i className="fas fa-trash"></i>
          </Button>
        </td>
      </tr>,
    ];
    children.forEach((child) => rows.push(...renderMenuRow(child, depth + 1)));
    return rows;
  };

  return (
    <div className="admin-content">
      <div className="content-header d-flex justify-content-between align-items-center mb-4">
        <h2>Kelola Menu</h2>
        <Button variant="primary" onClick={() => handleShowModal()}>
          <i className="fas fa-plus me-2"></i>Tambah Menu
        </Button>
      </div>

      {error && !showModal && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Label Menu</th>
                  <th>Tipe</th>
                  <th>URL / Konten</th>
                  <th>Urutan</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {topLevelMenus.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      Belum ada menu. Klik "Tambah Menu" untuk mulai.
                    </td>
                  </tr>
                ) : (
                  topLevelMenus.flatMap((menu) => renderMenuRow(menu, 0))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Tambah / Edit */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {currentMenu ? "Edit Menu" : "Tambah Menu"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>
                Label Menu <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="label"
                value={formData.label}
                onChange={handleChange}
                placeholder="Contoh: Tentang Kami"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Label Menu (English)</Form.Label>
              <Form.Control
                name="label_en"
                value={formData.label_en || ""}
                onChange={handleChange}
                placeholder="Example: About Us"
              />
              <Form.Text className="text-muted">
                Opsional. Ditampilkan ketika pengunjung memilih bahasa Inggris.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Parent Menu</Form.Label>
              <Form.Select
                name="parent_id"
                value={formData.parent_id}
                onChange={handleChange}
              >
                <option value="">— Tidak ada (menu utama) —</option>
                {parentOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Pilih parent jika ini adalah sub-menu (dropdown).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Tipe Menu <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="menu_type"
                value={formData.menu_type}
                onChange={handleChange}
              >
                {MENU_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {formData.menu_type === "static" ? (
              <Form.Group className="mb-3">
                <Form.Label>URL / Path</Form.Label>
                <Form.Control
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="Contoh: /contact  atau  https://example.com"
                />
                <Form.Text className="text-muted">
                  Gunakan path relatif (misal <code>/contact</code>) untuk
                  halaman internal, atau URL lengkap untuk link eksternal.
                </Form.Text>
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>
                  Konten yang ditampilkan <span className="text-danger">*</span>
                </Form.Label>
                {contentJenisList.length > 0 ? (
                  <Form.Select
                    name="content_jenis"
                    value={formData.content_jenis}
                    onChange={handleChange}
                    required={formData.menu_type === "content"}
                  >
                    <option value="">-- Pilih konten --</option>
                    {contentJenisList.map((item) => (
                      <option key={item.jenis} value={item.jenis}>
                        {item.label} ({item.jenis})
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <div
                    className="alert alert-warning py-2 mb-0"
                    style={{ fontSize: "0.875rem" }}
                  >
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Belum ada konten. Silakan buat konten terlebih dahulu di{" "}
                    <strong>Kelola Content</strong> sebelum membuat menu tipe
                    ini.
                  </div>
                )}
                <Form.Text className="text-muted">
                  Pilih konten yang sudah dibuat di menu{" "}
                  <strong>Kelola Content</strong>. Pengunjung yang mengklik menu
                  ini akan melihat isi konten tersebut.
                </Form.Text>
                {formData.content_jenis && (
                  <div
                    className="mt-2 p-2 bg-light rounded border"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <span className="text-muted">
                      URL yang akan terbentuk:{" "}
                    </span>
                    <strong>/halaman/{formData.content_jenis}</strong>
                  </div>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Buka di</Form.Label>
              <Form.Select
                name="target"
                value={formData.target}
                onChange={handleChange}
              >
                <option value="_self">Tab yang sama (_self)</option>
                <option value="_blank">Tab baru (_blank)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Urutan</Form.Label>
              <Form.Control
                type="number"
                name="urutan"
                value={formData.urutan}
                onChange={handleChange}
                min={0}
              />
              <Form.Text className="text-muted">
                Angka lebih kecil ditampilkan lebih dahulu.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                name="is_active"
                label="Menu Aktif"
                checked={formData.is_active}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Batal
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Menyimpan..." : "Simpan"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuManagement;
